import type { ReactNode } from 'react';
import { SemanticIcon, type SemanticIconName } from './SemanticIcon';
import { semanticAssets, type SemanticAssetName } from './semanticAssets';

export type SemanticStateKind = 'empty' | 'no-results' | 'loading' | 'error' | 'offline' | 'success' | 'notice';

const stateIcons: Record<SemanticStateKind, SemanticIconName> = {
  empty: 'inbox',
  'no-results': 'search',
  loading: 'sparkle',
  error: 'error',
  offline: 'offline',
  success: 'success',
  notice: 'info',
};

export interface SemanticStateProps {
  kind: SemanticStateKind;
  title: string;
  description?: string;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  compact?: boolean;
  className?: string;
  live?: 'polite' | 'assertive' | 'off';
  illustration?: SemanticAssetName;
}

export function SemanticState({
  kind,
  title,
  description,
  action,
  secondaryAction,
  compact = false,
  className = '',
  live = kind === 'error' || kind === 'success' ? 'polite' : 'off',
  illustration,
}: SemanticStateProps) {
  return (
    <section
      role="region"
      aria-label={title}
      className={`opp-semantic-state opp-semantic-state--${kind} ${compact ? 'opp-semantic-state--compact' : ''} ${className}`.trim()}
      aria-live={live === 'off' ? undefined : live}
      aria-busy={kind === 'loading' ? true : undefined}
    >
      {illustration ? (
        <img className="opp-semantic-state__illustration" src={semanticAssets[illustration]} alt="" aria-hidden="true" width={compact ? 120 : 176} height={compact ? 120 : 176} loading="lazy" decoding="async" />
      ) : (
        <span className="opp-semantic-state__visual" aria-hidden="true">
          <span className="opp-semantic-state__halo" />
          <SemanticIcon name={stateIcons[kind]} size={compact ? 28 : 42} className={kind === 'loading' ? 'opp-semantic-state__spin' : undefined} />
        </span>
      )}
      <div className="opp-semantic-state__copy">
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
      {(action || secondaryAction) && (
        <div className="opp-semantic-state__actions">
          {action}
          {secondaryAction}
        </div>
      )}
    </section>
  );
}

export default SemanticState;
